package com.rainbowclubs.rainbow.presentation.admin

import androidx.compose.animation.core.*
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.scale
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.rainbowclubs.rainbow.presentation.theme.RainbowClubTheme
import com.rainbowclubs.rainbow.presentation.theme.transparent
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import rainbowclubs.composeapp.generated.resources.*

@Composable
fun TakeAttendanceScreen(
    clubId: String,
    eventId: String?,
    viewModel: TakeAttendanceViewModel = koinViewModel(parameters = { parametersOf(clubId, eventId) }),
    navigateTo: (TakeAttendanceViewModel.NavigationEvent) -> Unit,
) {
    val uiState by viewModel.uiState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.navigationIntent.collect { intent ->
            navigateTo(intent)
        }
    }

    LaunchedEffect(uiState.status) {
        if (uiState.status is TakeAttendanceStatus.Success) {
            delay(1600)
            viewModel.resetScanner()
        }
    }

    TakeAttendanceContent(
        status = uiState.status,
        onBackClick = viewModel::navigateBack,
        onQrCodeScanned = viewModel::onQrCodeScanned,
        onTryAgainClick = viewModel::resetScanner,
    )
}

@Composable
private fun TakeAttendanceContent(
    status: TakeAttendanceStatus,
    onBackClick: () -> Unit,
    onQrCodeScanned: (String) -> Unit,
    onTryAgainClick: () -> Unit,
    cameraContent: @Composable BoxScope.() -> Unit = {
        QrCameraScanner(
            isScanningEnabled = status is TakeAttendanceStatus.Ready,
            onQrCodeScanned = onQrCodeScanned,
            modifier = Modifier
                .fillMaxSize()
                .padding(top = AttendanceTopBarHeight),
        )
    },
) {
    Scaffold(
        containerColor = MaterialTheme.colorScheme.transparent,
    ) { paddingValues ->
        Box(
            modifier = Modifier
                .padding(paddingValues)
                .fillMaxSize()
                .background(MaterialTheme.colorScheme.scrim),
        ) {
            if (status is TakeAttendanceStatus.Ready) {
                cameraContent()
            }

            ScannerOverlay()

            AttendanceTopBar(
                onBackClick = onBackClick,
                modifier = Modifier.align(Alignment.TopCenter),
            )

            when (status) {
                TakeAttendanceStatus.Ready -> Unit
                TakeAttendanceStatus.Signing -> SigningOverlay()
                TakeAttendanceStatus.Success -> SuccessOverlay()
                is TakeAttendanceStatus.Failure -> FailureOverlay(
                    reason = status.reason,
                    onTryAgainClick = onTryAgainClick.takeIf { status.canRetry },
                )
            }
        }
    }
}

private val AttendanceTopBarHeight = 72.dp

@Composable
private fun AttendanceTopBar(
    onBackClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier.fillMaxWidth(),
        color = MaterialTheme.colorScheme.scrim.copy(alpha = 0.48f),
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 8.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            IconButton(onClick = onBackClick) {
                Icon(
                    painter = painterResource(Res.drawable.ic_arrow_back),
                    contentDescription = stringResource(Res.string.back_icon_cd),
                    tint = MaterialTheme.colorScheme.surface,
                )
            }

            Spacer(modifier = Modifier.width(8.dp))

            Text(
                text = stringResource(Res.string.take_attendance),
                color = MaterialTheme.colorScheme.surface,
                style = MaterialTheme.typography.titleLarge,
            )
        }
    }
}

@Composable
private fun ScannerOverlay() {
    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val scanSize = minOf(maxWidth, maxHeight) * 0.72f
        val scrimColor = MaterialTheme.colorScheme.scrim.copy(alpha = 0.52f)

        Canvas(modifier = Modifier.fillMaxSize()) {
            val left = (size.width - scanSize.toPx()) / 2f
            val top = (size.height - scanSize.toPx()) / 2f
            val right = left + scanSize.toPx()
            val bottom = top + scanSize.toPx()

            drawRect(
                color = scrimColor,
                topLeft = Offset.Zero,
                size = androidx.compose.ui.geometry.Size(size.width, top),
            )
            drawRect(
                color = scrimColor,
                topLeft = Offset(0f, bottom),
                size = androidx.compose.ui.geometry.Size(size.width, size.height - bottom),
            )
            drawRect(
                color = scrimColor,
                topLeft = Offset(0f, top),
                size = androidx.compose.ui.geometry.Size(left, scanSize.toPx()),
            )
            drawRect(
                color = scrimColor,
                topLeft = Offset(right, top),
                size = androidx.compose.ui.geometry.Size(size.width - right, scanSize.toPx()),
            )
        }

        Box(
            modifier = Modifier
                .align(Alignment.Center)
                .size(scanSize)
                .border(
                    width = 2.dp,
                    color = MaterialTheme.colorScheme.surface,
                    shape = RoundedCornerShape(24.dp),
                ),
        ) {
            ScannerCorners(size = scanSize)
            ScannerLine(scanSize = scanSize)
        }

        Text(
            text = stringResource(Res.string.scan_attendee_qr_code),
            modifier = Modifier
                .align(Alignment.Center)
                .offset(y = scanSize / 2 + 28.dp),
            color = MaterialTheme.colorScheme.surface,
            style = MaterialTheme.typography.titleMedium,
            textAlign = TextAlign.Center,
        )
    }
}

@Composable
private fun ScannerCorners(size: Dp) {
    val color = MaterialTheme.colorScheme.primaryContainer

    Canvas(modifier = Modifier.fillMaxSize()) {
        val strokeWidth = 5.dp.toPx()
        val length = size.toPx() * 0.18f
        val inset = 8.dp.toPx()

        listOf(
            Offset(inset, inset) to Pair(Offset(length, inset), Offset(inset, length)),
            Offset(size.toPx() - inset, inset) to Pair(
                Offset(size.toPx() - length, inset),
                Offset(size.toPx() - inset, length),
            ),
            Offset(inset, size.toPx() - inset) to Pair(
                Offset(length, size.toPx() - inset),
                Offset(inset, size.toPx() - length),
            ),
            Offset(size.toPx() - inset, size.toPx() - inset) to Pair(
                Offset(size.toPx() - length, size.toPx() - inset),
                Offset(size.toPx() - inset, size.toPx() - length),
            ),
        ).forEach { (corner, endpoints) ->
            drawLine(color, corner, endpoints.first, strokeWidth, cap = StrokeCap.Round)
            drawLine(color, corner, endpoints.second, strokeWidth, cap = StrokeCap.Round)
        }
    }
}

@Composable
private fun ScannerLine(scanSize: Dp) {
    val infiniteTransition = rememberInfiniteTransition()
    val progress by infiniteTransition.animateFloat(
        initialValue = 0f,
        targetValue = 1f,
        animationSpec = infiniteRepeatable(
            animation = tween(durationMillis = 1500, easing = LinearEasing),
            repeatMode = RepeatMode.Reverse,
        ),
    )

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 18.dp)
            .offset(y = (scanSize - 36.dp) * progress + 18.dp)
            .height(2.dp)
            .background(MaterialTheme.colorScheme.primaryContainer, CircleShape),
    )
}

@Composable
private fun SigningOverlay() {
    StatusSurface {
        CircularProgressIndicator(
            color = MaterialTheme.colorScheme.primaryContainer,
            trackColor = MaterialTheme.colorScheme.surfaceVariant,
        )
        Spacer(modifier = Modifier.height(16.dp))
        Text(
            text = stringResource(Res.string.attendance_signing),
            color = MaterialTheme.colorScheme.onSurface,
            style = MaterialTheme.typography.titleLarge,
        )
    }
}

@Composable
private fun SuccessOverlay() {
    var visible by remember { mutableStateOf(false) }
    LaunchedEffect(Unit) {
        visible = true
    }
    val scale by animateFloatAsState(
        targetValue = if (visible) 1f else 0.72f,
        animationSpec = tween(durationMillis = 420),
    )

    StatusSurface {
        Surface(
            modifier = Modifier
                .size(104.dp)
                .scale(scale),
            shape = CircleShape,
            color = MaterialTheme.colorScheme.primaryContainer,
        ) {
            Box(contentAlignment = Alignment.Center) {
                Icon(
                    painter = painterResource(Res.drawable.ic_check),
                    contentDescription = stringResource(Res.string.attendance_success_icon_cd),
                    modifier = Modifier.size(64.dp),
                    tint = MaterialTheme.colorScheme.onPrimaryContainer,
                )
            }
        }

        Spacer(modifier = Modifier.height(18.dp))

        Text(
            text = stringResource(Res.string.attendance_signed),
            color = MaterialTheme.colorScheme.onSurface,
            style = MaterialTheme.typography.titleLarge,
        )
    }
}

@Composable
private fun FailureOverlay(
    reason: String,
    onTryAgainClick: (() -> Unit)?,
) {
    var visible by remember { mutableStateOf(false) }
    LaunchedEffect(Unit) {
        visible = true
    }
    val scale by animateFloatAsState(
        targetValue = if (visible) 1f else 0.72f,
        animationSpec = tween(durationMillis = 420),
    )

    StatusSurface {
        Surface(
            modifier = Modifier
                .size(104.dp)
                .scale(scale),
            shape = CircleShape,
            color = MaterialTheme.colorScheme.errorContainer,
        ) {
            Box(contentAlignment = Alignment.Center) {
                Icon(
                    painter = painterResource(Res.drawable.ic_cancel),
                    contentDescription = stringResource(Res.string.attendance_failure_icon_cd),
                    modifier = Modifier.size(64.dp),
                    tint = MaterialTheme.colorScheme.onErrorContainer,
                )
            }
        }

        Spacer(modifier = Modifier.height(18.dp))

        Text(
            text = reason,
            color = MaterialTheme.colorScheme.onSurface,
            style = MaterialTheme.typography.titleLarge,
            textAlign = TextAlign.Center,
        )

        if (onTryAgainClick != null) {
            Spacer(modifier = Modifier.height(16.dp))

            Button(onClick = onTryAgainClick) {
                Text(
                    text = stringResource(Res.string.attendance_try_again),
                    style = MaterialTheme.typography.titleMedium,
                )
            }
        }
    }
}

@Composable
private fun StatusSurface(content: @Composable ColumnScope.() -> Unit) {
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center,
    ) {
        Surface(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 36.dp),
            shape = RoundedCornerShape(28.dp),
            color = MaterialTheme.colorScheme.surface.copy(alpha = 0.92f),
            tonalElevation = 8.dp,
        ) {
            Column(
                modifier = Modifier.padding(horizontal = 24.dp, vertical = 28.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center,
                content = content,
            )
        }
    }
}

@Preview(name = "Take Attendance - Ready", widthDp = 390, heightDp = 844)
@Composable
private fun TakeAttendanceReadyPreview() {
    TakeAttendancePreview(status = TakeAttendanceStatus.Ready)
}

@Preview(name = "Take Attendance - Success", widthDp = 390, heightDp = 844)
@Composable
private fun TakeAttendanceSuccessPreview() {
    TakeAttendancePreview(status = TakeAttendanceStatus.Success)
}

@Preview(name = "Take Attendance - Failure", widthDp = 390, heightDp = 844)
@Composable
private fun TakeAttendanceFailurePreview() {
    TakeAttendancePreview(
        status = TakeAttendanceStatus.Failure(
            reason = "Attendee is already registered",
        )
    )
}

@Composable
private fun TakeAttendancePreview(status: TakeAttendanceStatus) {
    RainbowClubTheme {
        Surface {
            TakeAttendanceContent(
                status = status,
                onBackClick = {},
                onQrCodeScanned = {},
                onTryAgainClick = {},
                cameraContent = { PreviewCameraSurface() },
            )
        }
    }
}

@Composable
private fun PreviewCameraSurface() {
    val colorScheme = MaterialTheme.colorScheme

    Canvas(
        modifier = Modifier
            .fillMaxSize()
            .background(colorScheme.inverseSurface),
    ) {
        drawRect(color = colorScheme.inverseSurface)
        drawCircle(
            color = colorScheme.primary.copy(alpha = 0.18f),
            radius = size.minDimension * 0.42f,
            center = Offset(size.width * 0.18f, size.height * 0.24f),
        )
        drawCircle(
            color = colorScheme.tertiaryContainer.copy(alpha = 0.16f),
            radius = size.minDimension * 0.34f,
            center = Offset(size.width * 0.86f, size.height * 0.34f),
        )
        drawRect(
            color = colorScheme.surfaceVariant.copy(alpha = 0.14f),
            topLeft = Offset(0f, size.height * 0.58f),
            size = androidx.compose.ui.geometry.Size(size.width, size.height * 0.42f),
        )
        drawCircle(
            color = colorScheme.secondaryContainer.copy(alpha = 0.12f),
            radius = size.minDimension * 0.24f,
            center = Offset(size.width * 0.48f, size.height * 0.72f),
        )
    }
}
